import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  getCurrentUser,
  login as loginRequest,
} from '@/services/auth';
import type { LoginParams, User } from '@/types/api';

export const useAuthStore = defineStore('auth', () => {
  const currentUser = ref<User>();
  const loading = ref(false);
  const initialized = ref(false);

  const token = computed(() => localStorage.getItem('token') || '');
  const isAuthenticated = computed(() => Boolean(token.value && currentUser.value));

  async function login(values: LoginParams) {
    const response = await loginRequest(values);
    localStorage.setItem('token', response.data.token);
    currentUser.value = response.data.user;
    initialized.value = true;
    return response.data;
  }

  async function fetchCurrentUser(force = false) {
    if (initialized.value && !force) return currentUser.value;
    if (!token.value) {
      currentUser.value = undefined;
      initialized.value = true;
      return undefined;
    }

    loading.value = true;
    try {
      const response = await getCurrentUser();
      currentUser.value = response.data;
      initialized.value = true;
      return response.data;
    } catch (error) {
      currentUser.value = undefined;
      localStorage.removeItem('token');
      initialized.value = true;
      throw error;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    // JWT 为无状态凭证；退出只需清除本地会话，不请求不存在的后端注销接口。
    localStorage.removeItem('token');
    currentUser.value = undefined;
    initialized.value = true;
  }

  return {
    currentUser,
    loading,
    initialized,
    token,
    isAuthenticated,
    login,
    fetchCurrentUser,
    logout,
  };
});
